/**
 * voronoiBubblesPipeline — clusters of soap bubbles with thin-film
 * interference, drifting and merging/splitting, hundreds of small
 * bubbles plus larger central bubbles.
 *
 * Composition:
 *   1. Background — soft pearl gradient with FBM texture.
 *   2. Many tiny bubbles (~120) drifting around at varied scales.
 *   3. 8 larger central bubbles with strong iridescent coatings
 *      (thin-film interference pattern from film thickness).
 *   4. Bubble surface highlights — specular dot + rim.
 *   5. Bubble clusters — bubbles tend to group near attractors.
 *   6. Occasional merge/split — bubbles pulse size in/out.
 *
 * Audio reactivity:
 *   bass — pulses the largest bubbles, intensifies iridescence
 *   mid  — drifts bubbles around, shifts attraction centers
 *   high — sparkles and rim flicker
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface VoronoiBubblesPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float h11(float n) {
  return fract(sin(n * 91.345) * 47453.12);
}

float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float r = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    r += a * noise2(p);
    p = p * 2.03 + 7.1;
    a *= 0.5;
  }
  return r;
}

// Bubble color from thin-film thickness.
vec3 bubbleColor(float thickness, float seed) {
  // Multiple wavelengths.
  float ph = thickness + seed * 6.28;
  float r = 0.5 + 0.5 * cos(ph * 0.012 + 0.0);
  float g = 0.5 + 0.5 * cos(ph * 0.014 + 2.0);
  float b = 0.5 + 0.5 * cos(ph * 0.016 + 4.0);
  return vec3(r, g, b);
}

void main() {
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;

  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid  = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid  = pow(clamp(mid  * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // --- 1. Background pearl gradient ---
  float bgT = clamp((p.y + 0.8) / 1.6, 0.0, 1.0);
  vec3 bg = mix(vec3(0.06, 0.05, 0.10), vec3(0.18, 0.14, 0.20), bgT);
  bg += vec3(0.20, 0.15, 0.30) * fbm(p * 2.0 + vec2(uTime * 0.04, uTime * 0.03)) * 0.25;

  vec3 col = bg;

  // --- 2. Large central bubbles (8 of them) ---
  vec2 bigPos[8];
  bigPos[0] = vec2(-0.40, 0.30);
  bigPos[1] = vec2(0.35, 0.25);
  bigPos[2] = vec2(-0.10, -0.05);
  bigPos[3] = vec2(0.45, -0.30);
  bigPos[4] = vec2(-0.45, -0.35);
  bigPos[5] = vec2(0.10, 0.50);
  bigPos[6] = vec2(-0.25, -0.50);
  bigPos[7] = vec2(0.55, 0.05);

  for (int i = 0; i < 8; i++) {
    float fi = float(i);
    vec2 c = bigPos[i];
    // Drift.
    c += vec2(
      sin(uTime * (0.3 + fi * 0.07) + fi) * 0.05,
      cos(uTime * (0.4 + fi * 0.05) + fi * 1.3) * 0.05
    ) * (0.5 + mid * 0.5);
    float r = length(p - c);
    float radius = 0.08 + 0.04 * sin(uTime * (1.5 + fi * 0.4) + fi * 1.7) * (0.5 + bass * 0.5);

    if (r < radius) {
      // Inside bubble.
      float thickness = 600.0
        + r * 1500.0
        + sin(uTime * 0.8 + fi * 2.0) * 200.0
        + cos(c.x * 5.0 + c.y * 5.0) * 150.0;
      vec3 bcol = bubbleColor(thickness, fi * 0.3);
      // Bubble body — slightly translucent.
      float bodyT = smoothstep(radius, radius * 0.95, r);
      col = mix(col, bcol * 0.9, bodyT * 0.6);

      // Specular highlight (off-center dot).
      vec2 specPos = c + vec2(radius * 0.4, -radius * 0.4);
      float specD = length(p - specPos);
      float spec = exp(-specD * specD * 600.0);
      col += vec3(1.0) * spec * (1.0 + bass * 1.0);

      // Rim darkening — outside edge thin line.
      float rim = exp(-pow(r - radius, 2.0) * 4000.0);
      col += vec3(0.5, 0.4, 0.7) * rim * 0.8;

      // Inner rim — rainbow shimmer near edge.
      float innerRim = exp(-pow(r - radius * 0.95, 2.0) * 1200.0);
      vec3 rimColor = bubbleColor(thickness * 1.5, fi * 0.7);
      col += rimColor * innerRim * (0.6 + high * 0.6);
    } else if (r < radius + 0.015) {
      // Soft outer glow.
      float halo = exp(-(r - radius) * 60.0);
      vec3 haloCol = bubbleColor(800.0 + r * 1000.0, fi * 0.3);
      col += haloCol * halo * 0.3;
    }
  }

  // --- 3. Many tiny bubbles ---
  for (int i = 0; i < 120; i++) {
    float fi = float(i);
    float seed = h11(fi * 1.31);
    // Drift orbit.
    float t = uTime * (0.2 + seed * 0.4) + seed * 10.0;
    float orbitR = 0.3 + 0.4 * seed;
    float ang = seed * 6.28 + t * (0.3 + seed * 0.5);
    vec2 c2 = vec2(cos(ang), sin(ang)) * orbitR;
    // Slight wobble.
    c2 += vec2(
      sin(t * 2.0 + seed * 5.0) * 0.03,
      cos(t * 1.5 + seed * 4.0) * 0.03
    );
    float radius = 0.005 + seed * 0.018;
    float d2 = length(p - c2);
    if (d2 < radius + 0.005) {
      float thickness = 400.0 + seed * 600.0 + sin(t * 0.5 + seed * 4.0) * 200.0;
      vec3 bcol = bubbleColor(thickness, seed * 6.0);
      float bodyT = smoothstep(radius, radius * 0.8, d2);
      col = mix(col, bcol, bodyT * 0.5);
      // Tiny specular dot.
      float spec = exp(-pow(d2 - radius * 0.5, 2.0) * 2000.0);
      col += vec3(1.0) * spec * 0.6;
    }
  }

  // --- 4. Drift haze overlay ---
  float haze = fbm(p * 3.0 + vec2(uTime * 0.05, uTime * 0.04));
  col += vec3(0.30, 0.20, 0.50) * haze * 0.10;

  // Vignette.
  col *= 1.0 - dot(p, p) * 0.3;

  // Tone mapping + gamma.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}
`;

export function computeVoronoiBubblesBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.8);
  out[1] = 0.5 + 0.4 * Math.abs(Math.sin(t * 1.6));
  out[2] = 0.4 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.3 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 2.0 + 2.3);
  out[5] = 0.4 + 0.5 * Math.sin(t * 2.8 + 3.1);
  out[6] = 0.3 + 0.6 * Math.sin(t * 4.5 + 0.5);
  out[7] = 0.4 + 0.5 * Math.sin(t * 6.5 + 1.7);
  out[8] = 0.3 + 0.6 * Math.sin(t * 8.5 + 2.1);
}

export function computeVoronoiBubblesRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.012], [0.012, 0.040], [0.040, 0.080],
    [0.080, 0.180], [0.180, 0.300], [0.300, 0.460],
    [0.460, 0.640], [0.640, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0;
    let count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createVoronoiBubblesPipeline(gl: WebGL2RenderingContext): VoronoiBubblesPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program,
    vao,
    vbo,
    bands: new Float32Array(9),
  };
}

export function drawVoronoiBubbles(
  gl: WebGL2RenderingContext,
  pipe: VoronoiBubblesPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}